
import { Trans, useTranslation } from "react-i18next";
import NewPage from "../../../layouts/page/NewPage";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useState } from "react";
import MenuConfig from "../../../../constants/MenuConfig";
import Form, { DIRECT_CARD_ENTRY_ENABLED } from "./Form";
import { getId, sendToApi } from "../../../../utils/ApiUtils";
import Api from "../../../../constants/Api";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiError } from "../../../components/Common/ApiError";
import { warnAlert } from "../../../components/alert/defaultAlert";
import axiosInstance from "../../../../services/AxiosInstance";
import SubscriptionPaymentLinkModal from "../../../components/Custom/Charge/SubscriptionPaymentLinkModal";
import CustomerQuickCreateModal from "../../../components/Custom/Main/Customer/CustomerQuickCreateModal";
import ModalPage from "../../../components/Common/Modal/ModalPage";
import AddressNew from "../../../components/Custom/Common/Address/AddressNew";
import HintCallout from "../../../components/Common/HintCallout";
import { offerToOpenCash } from "../../../components/Custom/CashFlow/Cash/offerToOpenCash";

const SubscriberNew = () => {

    const { t } = useTranslation();
    const [entityData, setEntityData] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    // Quem chega de outra tela (hoje, a agenda) já traz o cliente escolhido e para onde
    // voltar depois. Sem isso o operador teria que localizar o cliente de novo e, ao
    // terminar, se achar sozinho na lista de assinantes em vez de voltar ao atendimento.
    const prefilledCustomer = location.state?.customer || null;
    const returnRoute = location.state?.returnTo || null;
    const [paymentToken, setPaymentToken] = useState(null);
    // Verdadeiro enquanto o cartão está sendo tokenizado no provedor. O salvar espera:
    // partir sem a hash faz o servidor recusar a assinatura, e o operador que digita
    // rápido chegava no botão antes de a hash existir.
    const [tokenizing, setTokenizing] = useState(false);
    const [reset, setReset] = useState(false);
    // Cobrança recém-criada, exibida logo após salvar no fluxo por link. Sem isso o
    // operador teria que sair do cadastro e procurar a assinatura na lista só para
    // achar o link que precisa mandar ao cliente ainda no balcão.
    const [newCharge, setNewCharge] = useState(null);
    const [newChargePhone, setNewChargePhone] = useState(null);
    // O nome do plano nao vem na cobranca e o formulario e limpo ao fechar o modal,
    // entao ele e guardado no momento em que o modal abre.
    const [newChargePlan, setNewChargePlan] = useState(null);
    // Guarda para onde ir quando o operador fechar o modal do link.
    const [afterLinkModal, setAfterLinkModal] = useState(null);
    const [quickCustomerOpen, setQuickCustomerOpen] = useState(false);
    // Cliente cujo endereço o modal está pedindo. Preenchido abre o modal direto no
    // formulário: antes havia uma pergunta "deseja cadastrar agora?" no meio, um clique a
    // mais para quem não tem outra saída a não ser informar o endereço.
    const [addressCustomerId, setAddressCustomerId] = useState(null);

    // Define o schema de validação do form.
    const formSchema = Yup.object().shape({
        customer: Yup.object().required(t('validation.required.customer')).nullable(),
        plan: Yup.object().required(t('validation.required.plan')).nullable(),
        employee: Yup.object().nullable().optional(),
        providerBillingType: Yup.string().required(t('validation.required.payment_method')),
        obs: Yup.string().nullable().optional(),
    });

    // Define o formik.
    const formik = useFormik({
        initialValues: {
            customer: prefilledCustomer,
            plan: null,
            employee: null,
            providerBillingType: 'CREDIT_CARD',
            obs: '',
            // Com a digitação do cartão oculta, toda assinatura nasce pelo link.
            paymentLinkRequested: !DIRECT_CARD_ENTRY_ENABLED,
            // Dados transitórios: o backend usa-os apenas para criar a assinatura e não os persiste.
            creditCard: { holderName: '', number: '', expiry: '', expiryMonth: '', expiryYear: '', ccv: '' },
            // Pacote cobrado na loja: pago agora no balcão, sem provedor. O Form liga sozinho
            // quando o tenant não tem provedor configurado.
            chargedAtStore: false,
            storePaymentMethod: null
        },
        validationSchema: formSchema,
        onSubmit: async values => {
            // O pacote nasce pago: sem a forma de pagamento o backend recusaria, e o aviso aqui
            // aponta o campo em vez de devolver a mensagem depois do clique.
            if (values.chargedAtStore && !values.storePaymentMethod?.value) {
                formik.setFieldError('storePaymentMethod', t('validation.required.payment_method'));
                formik.setSubmitting(false);
                return;
            }

            // O Checkout do Asaas exige endereço antes de criar o link. Interrompemos
            // somente quando faltar endereço e retomamos o mesmo submit após o modal salvar.
            // Como a checagem roda para qualquer provedor, o texto do modal não cita o Asaas.
            // O pacote da loja não tem link nem provedor, então não pede endereço.
            if (!values.chargedAtStore && values.paymentLinkRequested && values.customer?.value) {
                try {
                    const { data } = await axiosInstance.get(
                        `${Api.main.customer_address}/default/${values.customer.value}`);
                    const address = data || {};
                    const complete = address.street && address.number && address.neighborhood
                        && address.city && address.state && address.zipCode;
                    if (!complete) {
                        setAddressCustomerId(values.customer.value);
                        formik.setSubmitting(false);
                        return;
                    }
                } catch (error) {
                    // Endereço inexistente (404) abre o modal; falhas de rede continuam
                    // sendo exibidas normalmente para não mascarar um problema externo.
                    if (error?.response?.status !== 404) {
                        ApiError(error);
                        formik.setSubmitting(false);
                        return;
                    }
                    setAddressCustomerId(values.customer.value);
                    formik.setSubmitting(false);
                    return;
                }
            }

            if (!values.chargedAtStore
                    && values.providerBillingType === 'CREDIT_CARD'
                    && !values.paymentLinkRequested
                    && !paymentToken
                    && (!values.creditCard?.holderName
                        || !values.creditCard?.number
                        || !values.creditCard?.expiryMonth
                        || !values.creditCard?.expiryYear
                        || !values.creditCard?.ccv)) {
                // O número digitado só pode seguir para a Celcoin depois da tokenização
                // no navegador. Sem essa trava o backend recebe uma assinatura sem hash
                // e devolve uma mensagem genérica, embora o problema esteja no cartão.
                warnAlert(t('validation.custom.card_token_missing'));
                formik.setSubmitting(false);
                return;
            }

            let payload = {
                id: getId(entityData),
                customer: {
                    id: values.customer.value
                },
                plan: {
                    id: values.plan.value
                },
                employee: values.employee ? { id: values.employee.value } : null,
                obs: values.obs,
                providerBillingType: values.providerBillingType,
                // No fluxo por link não há token: o cliente informa o cartão na página
                // hospedada do provedor, e o backend cria a assinatura sem cobrança imediata.
                integrationPaymentToken: values.providerBillingType === 'CREDIT_CARD'
                    && !values.paymentLinkRequested ? paymentToken : null,
                creditCardHolderName: values.creditCard?.holderName || null,
                creditCardNumber: values.creditCard?.number || null,
                creditCardExpiryMonth: values.creditCard?.expiryMonth || null,
                creditCardExpiryYear: values.creditCard?.expiryYear || null,
                creditCardCcv: values.creditCard?.ccv || null,
                // O pacote não tem link: o pagamento é registrado na própria contratação.
                paymentLinkRequested: !values.chargedAtStore && Boolean(values.paymentLinkRequested),
                integrationId: entityData?.integrationId ?? null,
                chargedAtStore: Boolean(values.chargedAtStore),
                storePaymentMethod: values.chargedAtStore ? values.storePaymentMethod?.value : null
            }

            // Aciona a api
            sendToApi(Api.subscription.subscription, payload, (response) => {
                // Veio de outra tela: devolve o operador ao ponto de partida em vez de
                // deixá-lo num formulário em branco longe de onde estava trabalhando.
                const finish = () => {
                    if (returnRoute) {
                        navigate(returnRoute);
                    } else if (entityData == null) {
                        onReset();
                    } else {
                        navigate(MenuConfig.subscription.subscription.route);
                    }
                };

                if (!values.chargedAtStore && values.paymentLinkRequested) {
                    offerPaymentLink(response?.data, finish);
                    return;
                }

                finish();
            }, (error) => {
                formik.setSubmitting(false);
                // O pacote lança o pagamento no caixa de quem vende. Sem caixa aberto a
                // contratação inteira é desfeita no backend; oferecer abrir o caixa e repetir
                // evita o operador ter que sair do cadastro e preencher tudo de novo.
                const handled = values.chargedAtStore && offerToOpenCash({
                    error,
                    branchId: values.customer?.customer?.branch?.id,
                    question: t('cash_flow.open_cash_before_consumption'),
                    openingNote: t('cash_flow.automatic_opening_for_store_package'),
                    onOpened: () => formik.submitForm()
                });
                if (!handled) {
                    ApiError(error);
                }
            });
        },
    });

    /**
     * Busca a cobrança recém-criada da assinatura e abre o modal com as ações do link
     * (abrir, copiar, enviar por WhatsApp). O link só existe depois que o provedor cria
     * a primeira cobrança, então este é o primeiro momento em que ele pode ser entregue.
     *
     * Nada aqui pode impedir o operador de seguir: a assinatura já foi criada. Se a
     * cobrança ainda não estiver disponível (provedor lento, webhook a caminho), seguimos
     * o fluxo normal e o link continua acessível pela tela de cobranças.
     */
    const offerPaymentLink = (subscription, finish) => {
        if (!subscription?.id) {
            finish();
            return;
        }

        axiosInstance.get(`${Api.subscription.charge}/subscription/${subscription.id}/recent`)
            .then(({ data }) => {
                const charge = Array.isArray(data) ? data.find((item) => item.status !== 'PAID') : null;
                if (!charge) {
                    finish();
                    return;
                }

                setNewCharge(charge);
                setNewChargePhone(subscription.customer?.phone || null);
                setNewChargePlan(formik.values.plan?.label || null);
                setAfterLinkModal(() => finish);
            })
            .catch(() => finish());
    };

    // Fechar o modal é o fim do cadastro: só então o operador volta ao lugar de origem.
    const closePaymentLinkModal = () => {
        const finish = afterLinkModal;
        setNewCharge(null);
        setNewChargePhone(null);
        setNewChargePlan(null);
        setAfterLinkModal(null);
        finish?.();
    };

    /**
     * Nome do cliente para o aviso de endereço. O rótulo do select traz telefone ou CPF
     * entre parênteses, que só poluiria a frase; quando a opção não carrega o cadastro
     * (cliente vindo da agenda ou do cadastro rápido), o rótulo é o que há.
     */
    const customerDisplayName = (option) => {
        const customer = option?.customer;
        const name = customer?.fullName || [customer?.firstName, customer?.lastName].filter(Boolean).join(' ');
        return name || option?.label || '';
    };

    // Valida o form e em seguida submete os dados. Aciona o onSubmit do formik.
    const onSubmit = (e) => {
        e.preventDefault();
        formik.handleSubmit();
    }

    const onReset = () => {
        formik.resetForm();
        setPaymentToken(null);

        // Força o reset dos componentes de pagamento.
        setReset(!reset);
        
        formik.setFieldValue('customer', null);
        formik.setFieldValue('plan', null);
        formik.setFieldValue('employee', null);
        formik.setFieldValue('providerBillingType', 'CREDIT_CARD');
        formik.setFieldValue('obs', '');
        formik.setFieldValue('paymentLinkRequested', !DIRECT_CARD_ENTRY_ENABLED);
        formik.setFieldValue('chargedAtStore', false);
        formik.setFieldValue('storePaymentMethod', null);
    }

    return (
        <>
            <NewPage
                motherMenu={t('subscriptions')}
                childMenu={t('subscribers')}
                activeMenu={t('new')}
                child={
                    <Form
                        formik={formik}
                        entityData={entityData}
                        setPaymentToken={setPaymentToken}
                        setTokenizing={setTokenizing}
                        reset={reset}
                        onOpenQuickCustomer={() => setQuickCustomerOpen(true)}
                    />}
                setEntityData={setEntityData}
                path={Api.subscription.subscription}
                isSubmitting={formik.isSubmitting || (tokenizing && !formik.values.paymentLinkRequested)}
                handleSubmit={onSubmit}
                cancelRoute={returnRoute || MenuConfig.subscription.subscription.route}
            />

            <CustomerQuickCreateModal
                show={quickCustomerOpen}
                onHide={() => setQuickCustomerOpen(false)}
                onCreated={(customerOption) => {
                    formik.setFieldValue('customer', customerOption);
                    setQuickCustomerOpen(false);
                }}
            />

            <ModalPage
                title={
                    <>
                        <i className="fa-solid fa-location-dot me-2" aria-hidden="true"></i>
                        {t('subscription_address_required_title')}
                    </>
                }
                show={addressCustomerId !== null}
                setShow={(show) => {
                    if (!show) setAddressCustomerId(null);
                }}
                child={
                    <>
                        <HintCallout icon="fa-circle-info">
                            <Trans
                                i18nKey="subscription_address_required_hint"
                                values={{ name: customerDisplayName(formik.values.customer) }}
                                components={{ strong: <strong /> }}
                            />
                        </HintCallout>
                        <AddressNew
                            parentId={addressCustomerId}
                            gridRef={null}
                            entityId={null}
                            setEntityId={() => {}}
                            submitLabel={t('subscription_address_required_submit')}
                            setShow={(show) => {
                                if (!show) setAddressCustomerId(null);
                            }}
                            onSaved={() => {
                                setAddressCustomerId(null);
                                // O endereço já foi persistido; o submit refaz a consulta e
                                // então segue para a criação do Checkout.
                                formik.submitForm();
                            }}
                        />
                    </>
                }
                size="md"
            />

            <SubscriptionPaymentLinkModal
                charge={newCharge}
                setCharge={setNewCharge}
                phone={newChargePhone}
                planName={newChargePlan}
                onClose={closePaymentLinkModal}
            />
        </>
    );
}

export default SubscriberNew;
